import React from 'react';

const APP_NAME = 'Water Temp';

const WaterTempTerms = () => {
  return (
    <div className="min-h-screen bg-white text-gray-800 px-6 py-16">
      <h1 className="text-4xl font-bold mb-6 text-center">Terms of Use</h1>
      <p className="text-center text-gray-600 mb-4">Last Updated: August 2026</p>
      <hr className="my-6" />

      <div className="max-w-3xl mx-auto space-y-6 text-justify leading-relaxed">
        <p>
          These Terms of Use ("Terms") govern your use of the {APP_NAME} application ("the App"), developed
          and published by N1İnovasyon ("we", "us", or "our"). By downloading, installing, or using the App,
          you agree to be bound by these Terms. If you do not agree, please do not use the App.
        </p>

        <h2 className="text-xl font-semibold mt-8">1. Description of the Service</h2>
        <p>
          {APP_NAME} is an Apple Watch application that displays water temperature readings taken by the
          built-in water temperature sensor of supported Apple Watch models. The App is intended for
          recreational and informational use, such as swimming, open-water activities, and general curiosity.
        </p>

        <h2 className="text-xl font-semibold mt-8">2. Device Compatibility</h2>
        <p>
          Water temperature measurement requires an Apple Watch model equipped with a water temperature
          sensor. Supported models include:
        </p>
        <ul className="list-disc ml-6 space-y-1">
          <li>Apple Watch Ultra, Ultra 2, and Ultra 3</li>
          <li>Apple Watch Series 10</li>
          <li>Apple Watch Series 11</li>
        </ul>
        <p>
          Other Apple Watch models do not have the required sensor and cannot take water temperature
          readings. Please verify that your device is supported before purchasing. The App may also require
          a minimum watchOS version, as stated on its App Store page.
        </p>

        <h2 className="text-xl font-semibold mt-8">3. Purchases and Payment</h2>
        <p>
          The App offers a one-time <strong>lifetime purchase</strong> (currently $2.99, or the equivalent
          in your local currency) that unlocks the full version of the App. This is a non-consumable in-app
          purchase and is <strong>not a subscription</strong>: there are no recurring charges and nothing
          renews automatically.
        </p>
        <ul className="list-disc ml-6 space-y-1">
          <li>All purchases are processed by Apple through the App Store and are subject to Apple's terms.</li>
          <li>Prices may vary by country and currency and may change over time; the price shown in the App Store at the time of purchase applies.</li>
          <li>You can restore a previous purchase on any device signed in with the same Apple ID using the "Restore Purchases" option in the App.</li>
          <li>Refund requests are handled by Apple in accordance with App Store policies; we are unable to issue refunds directly.</li>
        </ul>
        <p>
          We reserve the right to change the price or offer additional paid features in the future. Features
          you have already purchased will remain available to you.
        </p>

        <h2 className="text-xl font-semibold mt-8">4. Accuracy of Measurements</h2>
        <p>
          Water temperature readings are provided by the sensor built into your Apple Watch and are
          subject to the sensor's technical limitations. Readings may be affected by factors such as the
          watch's initial temperature, the time the watch has been submerged, water flow, and how the watch
          is worn. Readings are provided <strong>for informational purposes only</strong> and may differ
          from measurements taken with professional or calibrated instruments.
        </p>

        <h2 className="text-xl font-semibold mt-8">5. Safety Notice</h2>
        <p>
          The App is not a safety device and must not be used as the sole basis for decisions involving
          personal safety, including but not limited to cold-water exposure, diving, or extreme-temperature
          activities. The App does not provide medical, diving, or professional advice. Always follow official
          safety guidance and use appropriate equipment for your activity. Apple's own guidance on the water
          resistance and operating limits of your Apple Watch always applies.
        </p>

        <h2 className="text-xl font-semibold mt-8">6. Acceptable Use</h2>
        <p>By using the App, you agree to:</p>
        <ul className="list-disc ml-6 space-y-1">
          <li>Use the App only for lawful purposes</li>
          <li>Not reverse engineer, decompile, or attempt to extract the source code of the App</li>
          <li>Not circumvent, disable, or interfere with purchase verification or any security feature of the App</li>
          <li>Not copy, resell, or redistribute the App or any part of it</li>
        </ul>

        <h2 className="text-xl font-semibold mt-8">7. Intellectual Property</h2>
        <p>
          The App and all of its content, including design, code, graphics, and logos, are owned by
          N1İnovasyon and are protected by copyright and other intellectual property laws. You are granted a
          limited, non-exclusive, non-transferable license to use the App on Apple devices that you own or
          control, in accordance with these Terms and Apple's App Store terms. No ownership rights are
          transferred to you.
        </p>

        <h2 className="text-xl font-semibold mt-8">8. Privacy</h2>
        <p>
          The App does not collect personal data, and sensor readings stay on your device. For details,
          please read our{' '}
          <a href="/privacy/watertemp" className="text-blue-600 hover:text-blue-800">
            Privacy Policy
          </a>.
        </p>

        <h2 className="text-xl font-semibold mt-8">9. Disclaimer of Warranties and Limitation of Liability</h2>
        <p>
          The App is provided "as is" and "as available" without warranties of any kind, express or implied.
          N1İnovasyon does not guarantee that the App will be error-free, uninterrupted, or that readings
          will be accurate. To the maximum extent permitted by law, N1İnovasyon shall not be liable for any
          direct, indirect, incidental, consequential, or special damages arising from your use of or
          inability to use the App, including any decisions made based on the readings it displays.
        </p>

        <h2 className="text-xl font-semibold mt-8">10. Apple-Specific Terms</h2>
        <p>
          These Terms are between you and N1İnovasyon only, not Apple Inc. Apple is not responsible for the
          App or its content and has no obligation to provide maintenance or support for it. Apple is a
          third-party beneficiary of these Terms and may enforce them against you. Your use of the App is
          also subject to Apple's{' '}
          <a
            href="https://www.apple.com/legal/internet-services/itunes/dev/stdeula/"
            className="text-blue-600 hover:text-blue-800"
            target="_blank"
            rel="noopener noreferrer"
          >
            Licensed Application End User License Agreement
          </a>.
        </p>

        <h2 className="text-xl font-semibold mt-8">11. Termination</h2>
        <p>
          We may suspend or terminate your access to the App if you violate these Terms. You may stop using
          the App at any time by deleting it from your devices.
        </p>

        <h2 className="text-xl font-semibold mt-8">12. Changes to These Terms</h2>
        <p>
          We may update these Terms from time to time. Significant changes will be announced within the App
          or on our website. Your continued use of the App after changes take effect constitutes acceptance
          of the updated Terms.
        </p>

        <h2 className="text-xl font-semibold mt-8">13. Governing Law</h2>
        <p>
          These Terms are governed by the laws of the Republic of Türkiye. The courts and enforcement
          offices of Antalya, Türkiye shall have exclusive jurisdiction over any disputes, without prejudice
          to any mandatory consumer protection rights you may have under the laws of your country of
          residence.
        </p>

        <h2 className="text-xl font-semibold mt-8">14. Contact</h2>
        <p>If you have any questions about these Terms, please contact us:</p>
        <p className="ml-4">
          Email: <a href="mailto:hi@n1inovasyon.com" className="text-blue-600">hi@n1inovasyon.com</a>
        </p>

        <p className="text-sm text-gray-500 mt-8">
          Apple, Apple Watch, and Apple Watch Ultra are trademarks of Apple Inc. {APP_NAME} is an
          independent product and is not affiliated with or endorsed by Apple Inc.
        </p>

        <div className="mt-12 p-4 bg-gray-100 rounded-lg">
          <p className="text-sm text-gray-600 text-center">
            By using this App, you confirm that you have read and agree to the Terms above.
          </p>
        </div>
      </div>
    </div>
  );
};

export default WaterTempTerms;
